// apps/.../wet/components/Contact.tsx
'use client';
import React, { useEffect, useRef, useState } from 'react';
import FadeInSection from './FadeInSection';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '@one-edge/state-management';
import {
  createCustomer,
  syncCustomerToJn,
  setCustomerType,
  assignCustomerOwners,
} from '@one-edge/state-management/slices/customers';
import { addCustomerActivity } from '@one-edge/state-management/slices/activities';
import { upsertTask } from '@one-edge/state-management/slices/tasks';
import { createClient } from '@one-edge/supabase-utils';

const supabase = createClient();

/** ORG + owner ids */
const ORG_ID = '4f8f4c2c-1a2b-4d3e-9f0a-1234567890ab';
const salesRepProfileId = '75764a3c-d78a-44c9-9473-3a688ee173cc'; // Alfredo Vasquez
const ownerUserIds = ['f910ac0e-6dae-4750-9590-e49eaa9b41b5'];
const WET_LEAD_SOURCE_ID = 80; // WET Contact Form

/* SMS recipients */
const SMS_RECIPIENTS = [
  { label: 'Huston', phone: '2108317691' },
  { label: 'Manuel', phone: '2102742778' },
];

/* WP marketing endpoint (defensive: all keys optional) */
const WP_MARKETING_ENDPOINT = 'https://wateredgetech.com/wp-json/wet/v1/marketing';

/* GA helper: local-only, ignore if embedded */
function ga(event: string, params?: Record<string, unknown>) {
  try {
    if (typeof window === 'undefined') return;
    if (window.top !== window.self) return;
    const g = (window as any).gtag;
    if (typeof g === 'function') g('event', event, params || {});
  } catch {}
}

function splitName(full: string) {
  const parts = full.trim().split(/\s+/);
  const first = parts[0] ?? '';
  const last = parts.slice(1).join(' ') || '';
  return { first, last };
}

/* --------- SMS helpers (similar style to WETSlugPageClient) --------- */

function buildSmsBodyForManagers(params: {
  fullName: string;
  phone: string | null;
  email: string;
  preferred: string;
  bestTime: string;
  notes: string;
  pageUrl: string | null;
  embed_location: string | null;
}) {
  const { fullName, phone, email, preferred, bestTime, notes, pageUrl, embed_location } =
    params;

  const urlSafe = pageUrl ? pageUrl.replace(/^https?:\/\//i, '') : null;

  const bits = [
    `New contact from Water Edge Technologies`,
    fullName ? `Name: ${fullName}` : null,
    phone ? `Call back: ${phone}` : null,
    email ? `Email: ${email}` : null,
    `Preferred: ${preferred}`,
    `Best time: ${bestTime}`,
    notes ? `Notes: ${notes}` : null,
    embed_location ? `Embed: ${embed_location}` : null,
    urlSafe ? `Page: ${urlSafe}` : null,
  ].filter(Boolean) as string[];

  const raw = bits
    .map((b) => b.replace(/\s+/g, ' ').trim())
    .join(' | ');

  const MAX_LEN = 450;
  return raw.length > MAX_LEN ? raw.slice(0, MAX_LEN - 3) + '...' : raw;
}

async function notifyManagersBySms(params: {
  fullName: string;
  email: string;
  phone: string | null;
  preferred: string;
  bestTime: string;
  notes: string;
  embed_location: string | null;
  customerId: string;
}) {
  if (typeof window === 'undefined') return;

  const { fullName, email, phone, preferred, bestTime, notes, embed_location, customerId } =
    params;

  const pageUrl = window.location.href;

  const message = buildSmsBodyForManagers({
    fullName,
    phone,
    email,
    preferred,
    bestTime,
    notes,
    pageUrl,
    embed_location,
  });

  const base =
    (process.env.NEXT_PUBLIC_WEBSITES_ORIGIN as string | undefined) ||
    window.location.origin;
  const apiUrl = `${base.replace(/\/$/, '')}/api/notify-advisor-sms`;

  for (const recipient of SMS_RECIPIENTS) {
    try {
      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          advisorId: recipient.label, // label used as id marker
          advisorPhone: recipient.phone,
          message,
          slug: 'wet_contact_form',
          customerId,
        }),
      });

      const text = await res.text();
      if (!res.ok) {
        console.error('notifyManagersBySms HTTP error', {
          to: recipient,
          status: res.status,
          body: text.slice(0, 400),
        });
        ga('notify_manager_sms_error', {
          to: recipient.label,
          phone: recipient.phone,
          customer_id: customerId,
        });
      } else {
        console.log('notifyManagersBySms success:', { to: recipient, text });
        ga('notify_manager_sms_success', {
          to: recipient.label,
          phone: recipient.phone,
          customer_id: customerId,
        });
      }
    } catch (err: any) {
      console.error('Failed to send manager SMS', { to: recipient, err });
      ga('notify_manager_sms_error', {
        to: recipient.label,
        phone: recipient.phone,
        customer_id: customerId,
        message: err?.message || 'unknown',
      });
    }
  }
}

/* --------- Email helper: calls /api/send-contact (generalized) --------- */

async function sendContactEmail(params: {
  fullName: string;
  email: string;
  phone: string | null;
  preferred: string;
  bestTime: string;
  notes: string;
  embed_location: string | null;
  utm: {
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_term?: string;
    utm_content?: string;
    gclid?: string;
  };
  customerId: string;
}) {
  if (typeof window === 'undefined') return;

  const {
    fullName,
    email,
    phone,
    preferred,
    bestTime,
    notes,
    embed_location,
    utm,
    customerId,
  } = params;

  const base =
    (process.env.NEXT_PUBLIC_WEBSITES_ORIGIN as string | undefined) ||
    window.location.origin;
  const apiUrl = `${base.replace(/\/$/, '')}/api/send-contact`;

  const payload = {
    fullName,
    email,
    phone,
    preferred,
    bestTime,
    notes,
    embed_location,
    utm,
    customerId,
    pageUrl: window.location.href,
    brand: 'wet',
    brandLabel: 'Water Edge Technologies',
  };

  const res = await fetch(apiUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`send-contact HTTP ${res.status}: ${text.slice(0, 400)}`);
  }
}

// ✅ 1. Accept 'isEmbedded' prop
const Contact: React.FC<{ isEmbedded?: boolean }> = ({ isEmbedded = false }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  /* WP copy overrides with sane defaults */
  const [title, setTitle] = useState('Schedule your water softener consultation');
  const [subtitle, setSubtitle] = useState(
    'Tell us about your home and we’ll reach out within one business day to review water softener and filtration options for softer water.'
  );
  const [successTitle, setSuccessTitle] = useState('Request received.');
  const [successSub, setSuccessSub] = useState(
    'A specialist will contact you using your preferred method.'
  );
  const [btnLabel, setBtnLabel] = useState('Request my call');
  const [consentText, setConsentText] = useState(
    'I agree to be contacted about my water analysis and options.'
  );

  /* Pull WP overrides once */
  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();
    (async () => {
      try {
        const r = await fetch(WP_MARKETING_ENDPOINT, {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
          signal: ctrl.signal,
        });
        if (!r.ok) return;
        const data = await r.json();
        const s = data?.sections || data?.footer || {};
        const c = s?.contact || s;
        const v = (x?: string) =>
          typeof x === 'string' && x.trim() ? x.trim() : undefined;
        if (cancelled) return;

        setTitle((t) => v(c?.contact_title) || v(c?.title) || t);
        setSubtitle((t) => v(c?.contact_subtitle) || v(c?.subtitle) || t);
        setSuccessTitle(
          (t) => v(c?.contact_success_title) || v(c?.success_title) || t
        );
        setSuccessSub(
          (t) =>
            v(c?.contact_success_subtitle) || v(c?.success_subtitle) || t
        );
        setBtnLabel((t) => v(c?.contact_button) || v(c?.button_label) || t);
        setConsentText(
          (t) => v(c?.contact_consent) || v(c?.consent_text) || t
        );
      } catch {}
    })();
    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, []);

  /* Section impression once */
  const sectionRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    let fired = false;
    const io = new IntersectionObserver(
      (entries) => {
        if (fired) return;
        if (entries.some((e) => e.isIntersecting)) {
          fired = true;
          ga('section_view', { section: 'contact' });
          io.disconnect();
        }
      },
      { threshold: 0.35 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  async function getStatusId() {
    const { data: st } = await supabase
      .from('statuses')
      .select('id')
      .eq('organization_id', ORG_ID)
      .eq('name', 'To Do')
      .maybeSingle();
    if (st?.id) return st.id;

    const { data: anySt } = await supabase
      .from('statuses')
      .select('id')
      .eq('organization_id', ORG_ID)
      .limit(1)
      .maybeSingle();
    if (anySt?.id) return anySt.id;

    throw new Error('No status found for tasks in this organization.');
  }

  return (
    // ✅ 2. Conditionally set padding on the <section>
    <section
      id="contact"
      ref={sectionRef}
      className={
        isEmbedded
          ? 'bg-slate-800/30 p-4' // Minimal padding
          : 'py-24 bg-slate-800/30' // Original padding
      }
    >
      <FadeInSection>
        {/* ✅ 3. Conditionally set container class */}
        <div
          className={
            isEmbedded
              ? 'text-center' // No container/padding
              : 'container mx-auto px-6 text-center' // Original container
          }
        >
          <h2 className="text-4xl font-bold">{title}</h2>
          {/* ✅ 4. Conditionally set margins AND max-width */}
          <p
            className={
              isEmbedded
                ? 'text-gray-400 mx-auto mt-4 mb-6' // Tighter margin, no max-width
                : 'text-gray-400 max-w-2xl mx-auto mt-4 mb-10' // Original margin
            }
          >
            {subtitle}
          </p>

          {/* ✅ 5. Conditionally set padding AND max-width on the form card */}
          <div
            className={
              isEmbedded
                ? 'mx-auto bg-slate-800/50 p-6 rounded-lg text-left' // Tighter padding, no max-width
                : 'max-w-xl mx-auto bg-slate-800/50 p-8 rounded-lg text-left' // Original padding
            }
          >
            {submitted ? (
              <div>
                <p className="text-teal-300 font-semibold text-lg">
                  {successTitle}
                </p>
                <p className="text-slate-300 mt-2">{successSub}</p>
              </div>
            ) : (
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  setErr(null);
                  setLoading(true);

                  const form = new FormData(e.currentTarget as HTMLFormElement);
                  const fullName = String(form.get('fullName') || '').trim();
                  const email = String(form.get('email') || '').trim();
                  const phoneRaw = String(form.get('phone') || '').trim() || null;
                  const phone =
                    phoneRaw && phoneRaw.replace(/[^\d]/g, '').length > 0
                      ? phoneRaw.replace(/[^\d]/g, '')
                      : null;
                  const preferred = String(form.get('preferred') || 'phone');
                  const bestTime = String(form.get('bestTime') || 'afternoon');
                  const notes = String(form.get('notes') || '').trim();
                  const consent = form.get('consent') ? true : false;

                  // capture tracking params from iframe URL
                  const url = new URL(window.location.href);
                  const utm = {
                    utm_source:
                      url.searchParams.get('utm_source') || undefined,
                    utm_medium:
                      url.searchParams.get('utm_medium') || undefined,
                    utm_campaign:
                      url.searchParams.get('utm_campaign') || undefined,
                    utm_term: url.searchParams.get('utm_term') || undefined,
                    utm_content:
                      url.searchParams.get('utm_content') || undefined,
                    gclid: url.searchParams.get('gclid') || undefined,
                  };

                  let embed_location: string | null = null;
                  if (
                    typeof window !== 'undefined' &&
                    window.top !== window.self
                  ) {
                    embed_location =
                      document.referrer || 'Unknown (Referrer hidden)';
                  }

                  ga('generate_lead', {
                    method: 'contact_form',
                    location: 'contact_section',
                    preferred,
                    best_time: bestTime,
                    consent,
                    embed_location: embed_location || undefined,
                    ...utm,
                  });

                  try {
                    const { first, last } = splitName(fullName);

                    // 1) Create customer (Set Sales Rep)
                    const created = await dispatch(
                      createCustomer({
                        __skipSync: true,
                        organization_id: ORG_ID,
                        display_name:
                          fullName || email || 'New Water Lead',
                        first_name: first,
                        last_name: last,
                        email,
                        mobile_phone: phone,
                        address_street: null,
                        address_city: null,
                        address_state: null,
                        address_zip: null,
                        sales_rep_id: salesRepProfileId, // Alfredo
                        notes: null,
                        status_name: 'New Lead', // Match spin wheel
                      })
                    ).unwrap();

                    // 2) Set Type = Lead Tracker
                    await dispatch(
                      setCustomerType({
                        customerId: created.id,
                        organizationId: ORG_ID,
                        type: 'Lead Tracker',
                      })
                    ).unwrap();

                    // 3) Assign owners (John etc.)
                    await dispatch(
                      assignCustomerOwners({
                        customerId: created.id,
                        userIds: ownerUserIds,
                      })
                    ).unwrap();

                    // 4) Sync to JobNimbus (Set Source ID)
                    await dispatch(
                      syncCustomerToJn({
                        id: created.id,
                        contactOverrides: {
                          source: WET_LEAD_SOURCE_ID, // 80
                        },
                      })
                    ).unwrap();

                    // 5) Activity note
                    const note = [
                      'New inbound contact from website.',
                      `Preferred contact: ${preferred}`,
                      `Best time: ${bestTime}`,
                      `Consent: ${consent ? 'Yes' : 'No'}`,
                      phone ? `Phone: ${phone}` : null,
                      email ? `Email: ${email}` : null,
                      notes ? `Notes: ${notes}` : null,
                      embed_location
                        ? `Embed Location: ${embed_location}`
                        : null,
                      utm.gclid ? `GCLID: ${utm.gclid}` : null,
                      utm.utm_source
                        ? `UTM Source: ${utm.utm_source}`
                        : null,
                      utm.utm_medium
                        ? `UTM Medium: ${utm.utm_medium}`
                        : null,
                      utm.utm_campaign
                        ? `UTM Campaign: ${utm.utm_campaign}`
                        : null,
                      utm.utm_term ? `UTM Term: ${utm.utm_term}` : null,
                      utm.utm_content
                        ? `UTM Content: ${utm.utm_content}`
                        : null,
                    ]
                      .filter(Boolean)
                      .join('\n');

                    await dispatch(
                      addCustomerActivity({
                        organization_id: ORG_ID,
                        customer_id: created.id,
                        note,
                        description: null,
                        typeName: 'Note',
                        task_id: null,
                      })
                    ).unwrap();

                    // 6) Task (Assign to Owner)
                    const status_id = await getStatusId();
                    await dispatch(
                      upsertTask({
                        organization_id: ORG_ID,
                        task_name: `Call lead: ${
                          fullName || email || 'New Water Lead'
                        }`,
                        description:
                          'Follow up on water analysis request and schedule visit.',
                        customer_id: created.id,
                        assigned_to_user_id: ownerUserIds[0], // John
                        priority: 'High',
                        status_id,
                      })
                    ).unwrap();

                    // 7) Fire SMS to Huston & Manuel (non-blocking if they fail)
                    try {
                      await notifyManagersBySms({
                        fullName,
                        email,
                        phone,
                        preferred,
                        bestTime,
                        notes,
                        embed_location,
                        customerId: created.id,
                      });
                    } catch (smsErr: any) {
                      console.error('SMS notify error', smsErr);
                    }

                    // 8) Send email via generalized send-contact API (non-blocking on error)
                    try {
                      await sendContactEmail({
                        fullName,
                        email,
                        phone,
                        preferred,
                        bestTime,
                        notes,
                        embed_location,
                        utm,
                        customerId: created.id,
                      });
                    } catch (mailErr: any) {
                      console.error('Email notify error', mailErr);
                      ga('contact_email_error', {
                        message: mailErr?.message || 'unknown',
                        customer_id: created.id,
                      });
                    }

                    ga('form_submit_success', {
                      form_id: 'contact_form',
                      location: 'contact_section',
                      customer_id: created.id,
                      embed_location: embed_location || undefined,
                      ...utm,
                    });

                    setSubmitted(true);
                  } catch (e: any) {
                    setErr(e?.message || 'Error submitting request.');
                    ga('form_submit_error', {
                      form_id: 'contact_form',
                      location: 'contact_section',
                      message: e?.message || 'unknown',
                      embed_location: embed_location || undefined,
                    });
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    name="fullName"
                    type="text"
                    placeholder="Full name"
                    required
                    className="w-full p-3 rounded bg-slate-700 border border-slate-600 focus:outline-none focus:ring-2 focus:ring-teal-500"
                    aria-label="Full name"
                  />
                  <input
                    name="email"
                    type="email"
                    placeholder="Email"
                    required
                    className="w-full p-3 rounded bg-slate-700 border border-slate-600 focus:outline-none focus:ring-2 focus:ring-teal-500"
                    aria-label="Email"
                  />
                  <input
                    name="phone"
                    type="tel"
                    placeholder="Phone (optional)"
                    className="w-full p-3 rounded bg-slate-700 border border-slate-600 focus:outline-none focus:ring-2 focus:ring-teal-500 md:col-span-2"
                    aria-label="Phone"
                  />
                </div>

                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <select
                    name="preferred"
                    aria-label="Preferred contact method"
                    className="w-full p-3 rounded bg-slate-700 border border-slate-600 focus:outline-none focus:ring-2 focus:ring-teal-500"
                    defaultValue="phone"
                  >
                    <option value="phone">Preferred: Phone call</option>
                    <option value="text">Text message</option>
                    <option value="email">Email</option>
                  </select>

                  <select
                    name="bestTime"
                    aria-label="Best time to contact"
                    className="w-full p-3 rounded bg-slate-700 border border-slate-600 focus:outline-none focus:ring-2 focus:ring-teal-500"
                    defaultValue="afternoon"
                  >
                    <option value="morning">Best time: Morning</option>
                    <option value="afternoon">Best time: Afternoon</option>
                    <option value="evening">Best time: Evening</option>
                  </select>
                </div>

                <textarea
                  name="notes"
                  rows={4}
                  placeholder="Anything specific you want us to check? (optional)"
                  className="w-full mt-4 p-3 rounded bg-slate-700 border border-slate-600 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  aria-label="Notes"
                />

                <label className="flex items-start gap-3 mt-4 text-sm text-slate-300">
                  <input
                    name="consent"
                    type="checkbox"
                    className="mt-1 accent-teal-500"
                    required
                  />
                  {consentText}
                </label>

                {err && (
                  <p className="mt-3 p-3 bg-red-100 text-red-700 border border-red-300 rounded text-sm">
                    {err}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full mt-6 glow-button text-white font-bold py-3 px-6 rounded-lg disabled:opacity-60"
                >
                  {loading ? 'Submitting…' : btnLabel}
                </button>

                <p className="text-xs text-slate-500 mt-3">
                  We respect your time and privacy. No spam. You can opt out at
                  any time.
                </p>
              </form>
            )}
          </div>
        </div>
      </FadeInSection>
    </section>
  );
};

export default Contact;
